import type {
  DiagramElement,
  PathEnding,
  PathElement,
  Point,
  RinkSide,
  RinkTemplate,
} from "../domain/drill";
import { renderPathGeometry, trimPathEnd } from "../lib/path-rendering";
import { normalizedSelectionBox, type SelectionBox } from "../lib/selection";
import { HockeyGoalSymbol } from "./HockeyGoalSymbol";

type Props = {
  template: RinkTemplate;
  rinkSide?: RinkSide;
  elements: DiagramElement[];
  selectedId?: string;
  selectedIds?: string[];
  selectionBox?: SelectionBox;
  interactive?: boolean;
  onPointerDown?: (
    point: Point,
    event: React.PointerEvent<SVGSVGElement>,
  ) => void;
  onPointerMove?: (
    point: Point,
    event: React.PointerEvent<SVGSVGElement>,
  ) => void;
  onPointerUp?: (
    point: Point,
    event: React.PointerEvent<SVGSVGElement>,
  ) => void;
  onElementPointerDown?: (
    id: string,
    event: React.PointerEvent<Element>,
  ) => void;
  onTextChange?: (id: string, value: string) => void;
};

export function rinkViewport(template: RinkTemplate, side: RinkSide = "left") {
  if (template === "half")
    return { x: side === "left" ? 0 : 500, y: 0, width: 500, height: 500 };
  if (template === "zone")
    return { x: side === "left" ? 0 : 635, y: 0, width: 365, height: 500 };
  if (template === "third") return { x: 365, y: 0, width: 270, height: 500 };
  return { x: 0, y: 0, width: 1000, height: 500 };
}

function eventPoint(
  event: React.PointerEvent<SVGSVGElement>,
  template: RinkTemplate,
  side: RinkSide,
): Point {
  const rect = event.currentTarget.getBoundingClientRect();
  const view = rinkViewport(template, side);
  return {
    x:
      view.x +
      Math.max(
        0,
        Math.min(
          view.width,
          ((event.clientX - rect.left) / rect.width) * view.width,
        ),
      ),
    y:
      view.y +
      Math.max(
        0,
        Math.min(
          view.height,
          ((event.clientY - rect.top) / rect.height) * view.height,
        ),
      ),
  };
}

function FaceoffCircle({
  x,
  y,
  color = "red",
  markings = true,
  radius = 60,
}: {
  x: number;
  y: number;
  color?: "red" | "blue";
  markings?: boolean;
  radius?: number;
}) {
  const className = color === "blue" ? "rink-blue thin" : "rink-red thin";
  return (
    <g aria-hidden="true">
      <circle
        className={className}
        data-faceoff-circle={color}
        cx={x}
        cy={y}
        r={radius}
      />
      <circle
        className={`faceoff-dot ${color === "blue" ? "blue" : ""}`}
        cx={x}
        cy={y}
        r="5.5"
      />
      {markings && (
        <>
          <path
            className={className}
            d={`M${x - 9} ${y - radius}v-10m18 10v-10M${x - 9} ${y + radius}v10m18-10v10`}
          />
          <path
            className={`${className} faceoff-hashmarks`}
            data-faceoff-hashmarks="true"
            d={`M${x - 9} ${y - 5}H${x - 24}M${x - 9} ${y - 5}V${y - 20}M${x + 9} ${y - 5}H${x + 24}M${x + 9} ${y - 5}V${y - 20}M${x - 9} ${y + 5}H${x - 24}M${x - 9} ${y + 5}V${y + 20}M${x + 9} ${y + 5}H${x + 24}M${x + 9} ${y + 5}V${y + 20}`}
          />
        </>
      )}
    </g>
  );
}

function OfficialRink({ blank }: { blank: boolean }) {
  return (
    <>
      <rect className="ice" x="8" y="8" width="984" height="484" rx="92" />
      <rect
        x="8"
        y="8"
        width="984"
        height="484"
        rx="92"
        fill="url(#ice-grid)"
      />
      {!blank && (
        <>
          <line
            className="rink-red center"
            x1="500"
            y1="12"
            x2="500"
            y2="488"
          />
          <line className="center-dashes" x1="500" y1="12" x2="500" y2="488" />
          <line className="rink-blue" x1="365" y1="12" x2="365" y2="488" />
          <line className="rink-blue" x1="635" y1="12" x2="635" y2="488" />
          <line
            className="rink-red thin goal-line"
            x1="75"
            y1="14"
            x2="75"
            y2="486"
          />
          <path
            className="rink-red thin goalie-trapezoid"
            d="M75 190L12 175M75 310L12 325M925 190L988 175M925 310L988 325"
            clipPath="url(#rink-inner-clip)"
          />
          <line
            className="rink-red thin goal-line"
            x1="925"
            y1="14"
            x2="925"
            y2="486"
          />
          <FaceoffCircle x={190} y={130} radius={60} />
          <FaceoffCircle x={190} y={370} radius={60} />
          <FaceoffCircle x={810} y={130} radius={60} />
          <FaceoffCircle x={810} y={370} radius={60} />
          <image
            className="center-logo"
            href="/brand/puckly-logo-white.png"
            x="456"
            y="228"
            width="88"
            height="44"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          />
          <FaceoffCircle
            x={500}
            y={250}
            color="blue"
            markings={false}
            radius={60}
          />
          {[
            { x: 392, y: 130 },
            { x: 392, y: 370 },
            { x: 608, y: 130 },
            { x: 608, y: 370 },
          ].map((spot) => (
            <circle
              key={`${spot.x}-${spot.y}`}
              className="neutral-dot"
              cx={spot.x}
              cy={spot.y}
              r="5.5"
            />
          ))}
          <path className="crease" d="M75 224H104A45 45 0 0 1 104 276H75Z" />
          <path className="crease" d="M925 224H896A45 45 0 0 0 896 276H925Z" />
          <path
            className="crease-marks"
            d="M104 224v5M104 276v-5M896 224v5M896 276v-5"
          />
        </>
      )}
    </>
  );
}

function legacyEnding(kind: PathElement["kind"]): PathEnding {
  if (kind === "shot") return "shot";
  if (kind === "stop" || kind === "pressure") return "stop";
  if (kind === "direction") return "direction";
  if (kind === "pivot") return "pivot";
  return "arrow";
}

function pathEndDirection(element: PathElement) {
  const end = element.points.at(-1);
  let before =
    element.kind === "straight" ? element.points[0] : element.points.at(-2);
  if (!end || !before) return undefined;
  for (
    let index = element.kind === "straight" ? -1 : element.points.length - 2;
    index >= 0;
    index -= 1
  ) {
    before = element.points[index];
    if (Math.hypot(end.x - before.x, end.y - before.y) >= 24) break;
  }
  const dx = end.x - before.x;
  const dy = end.y - before.y;
  return { end, angle: (Math.atan2(dy, dx) * 180) / Math.PI };
}

function PathEndingSymbol({ element }: { element: PathElement }) {
  const direction = pathEndDirection(element);
  if (!direction) return null;
  const ending = element.ending ?? legacyEnding(element.kind);
  const transform = `translate(${direction.end.x} ${direction.end.y}) rotate(${direction.angle})`;
  if (ending === "none") return null;
  if (ending === "stop")
    return (
      <g
        className="path-ending stop-ending ending-symbol"
        transform={transform}
        stroke={element.color}
      >
        <path d="M-5-12V12M3-12V12" />
      </g>
    );
  if (ending === "shot")
    return (
      <g
        className="path-ending shot-ending"
        transform={transform}
        stroke={element.color}
      >
        <path className="ending-mask" d="M-26 0H2" />
        <path
          className="ending-symbol"
          d="M-23-4H-10M-23 4H-10M-10-8L1 0-10 8"
        />
      </g>
    );
  if (ending === "pivot")
    return (
      <g
        className="path-ending pivot-ending ending-symbol"
        transform={transform}
        stroke={element.color}
      >
        <path className="pivot-turn" d="M-8 3A9 9 0 1 1 0 9" />
        <path
          className="pivot-arrowhead"
          d="M-5 9L2 5L2 13Z"
          fill={element.color}
          stroke="none"
        />
      </g>
    );
  if (ending === "direction")
    return (
      <path
        className="ending-symbol direction-ending"
        transform={transform}
        d="M2 0L-14-9V9Z"
        fill={element.color}
      />
    );
  return (
    <path
      className="path-ending ending-symbol"
      transform={transform}
      d="M-15-10L0 0-15 10"
      stroke={element.color}
    />
  );
}

function Element({
  element,
  selected,
  onPointerDown,
  onTextChange,
}: {
  element: DiagramElement;
  selected: boolean;
  onPointerDown?: Props["onElementPointerDown"];
  onTextChange?: Props["onTextChange"];
}) {
  if (element.type === "path") {
    const lineKind = [
      "shot",
      "stop",
      "direction",
      "pivot",
      "pressure",
    ].includes(element.kind)
      ? "free"
      : element.kind;
    const ending = element.ending ?? legacyEnding(element.kind);
    const renderPoints =
      ending === "pivot" ? trimPathEnd(element.points, 12) : element.points;
    const geometry = renderPathGeometry(lineKind, renderPoints);
    const strokeWidth = 2.5 * element.width;
    return (
      <g
        className={selected ? "selected" : ""}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <path
          className={`diagram-path ${element.kind}`}
          d={geometry.primary}
          stroke={element.color}
          strokeWidth={strokeWidth}
          strokeDasharray={geometry.dash}
        />
        {geometry.secondary && (
          <path
            className={`diagram-path ${element.kind} secondary`}
            d={geometry.secondary}
            stroke={element.color}
            strokeWidth={strokeWidth}
          />
        )}
        {geometry.ticks && (
          <path
            className="diagram-path lateral-ticks"
            d={geometry.ticks}
            stroke={element.color}
            strokeWidth={strokeWidth}
          />
        )}
        <PathEndingSymbol element={element} />
      </g>
    );
  }
  if (element.type === "text") {
    if (selected)
      return (
        <foreignObject
          x={element.point.x - 5}
          y={element.point.y - element.fontSize - 6}
          width="242"
          height="48"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div className="rink-text-editor">
            <button
              type="button"
              className="rink-text-drag"
              aria-label="Drag label to move"
              title="Drag to move label"
              onPointerDown={(event) => onPointerDown?.(element.id, event)}
            >
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="7" cy="6" r="1.3" />
                <circle cx="13" cy="6" r="1.3" />
                <circle cx="7" cy="10" r="1.3" />
                <circle cx="13" cy="10" r="1.3" />
                <circle cx="7" cy="14" r="1.3" />
                <circle cx="13" cy="14" r="1.3" />
              </svg>
            </button>
            <input
              className="rink-text-input"
              autoFocus
              value={element.value}
              aria-label="Diagram text"
              onChange={(event) =>
                onTextChange?.(element.id, event.target.value)
              }
            />
          </div>
        </foreignObject>
      );
    return (
      <g onPointerDown={(event) => onPointerDown?.(element.id, event)}>
        <rect
          className="text-box-hit"
          x={element.point.x - 5}
          y={element.point.y - element.fontSize}
          width={Math.max(55, element.value.length * element.fontSize * 0.62)}
          height={element.fontSize * 1.35}
          rx="4"
        />
        <text
          className="diagram-text"
          x={element.point.x}
          y={element.point.y}
          fill={element.color}
          fontSize={element.fontSize}
        >
          {element.value}
        </text>
      </g>
    );
  }
  const { x, y } = element.point;
  const scale = element.size ?? 1;
  const markerTransform = `translate(${x} ${y}) rotate(${element.rotation ?? 0}) scale(${scale})`;
  if (element.kind === "puck")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="16" />
        <circle r="5" fill={element.color} />
      </g>
    );
  if (element.kind === "puckCluster")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="24" />
        {[
          [0, 0],
          [-9, -7],
          [9, -7],
          [-9, 8],
          [9, 8],
          [0, 15],
        ].map(([cx, cy], index) => (
          <circle key={index} cx={cx} cy={cy} r="4.2" fill={element.color} />
        ))}
      </g>
    );
  if (element.kind === "cone")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="20" />
        <path
          d="M-9 11L0-13 9 11Z"
          fill={element.color}
          stroke="#fff"
          strokeWidth="1.5"
        />
      </g>
    );
  if (element.kind === "goal")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="28" />
        <HockeyGoalSymbol color={element.color} />
      </g>
    );
  if (element.kind === "tire")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="22" />
        {element.tireStyle === "solid" ? (
          <circle className="solid-tire" r="10" fill="#111111" />
        ) : (
          <>
            <circle r="14" fill="none" stroke={element.color} strokeWidth="6" />
            <circle r="4" fill={element.color} />
          </>
        )}
      </g>
    );
  if (element.kind === "barrier")
    return (
      <g
        className={`marker-barrier${selected ? " selected" : ""}`}
        transform={`translate(${x} ${y}) rotate(${element.rotation ?? 0})`}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="28" />
        <rect
          x={-(element.length ?? 52) / 2}
          y="-5"
          width={element.length ?? 52}
          height="10"
          rx="2"
          fill={element.color}
        />
      </g>
    );
  if (element.kind === "stick")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="28" />
        <path
          d="M0-22V22"
          fill="none"
          stroke={element.color}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    );
  if (element.kind === "stickTires")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="33" />
        <path
          d="M-24 0H24"
          stroke={element.color}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx="-24" r="6" fill={element.color} />
        <circle cx="24" r="6" fill={element.color} />
      </g>
    );
  if (element.kind === "dummy")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="27" />
        <path
          d="M7-17H-7V17H7M-7 0H3Q8 0 10-5"
          fill="none"
          stroke={element.color}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    );
  if (element.kind === "basket")
    return (
      <g
        className={`marker-basket${selected ? " selected" : ""}`}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="34" />
        <rect
          className="basket-body"
          x="-26"
          y="-19"
          width="52"
          height="38"
          rx="6"
          fill="none"
          stroke={element.color}
          strokeWidth="4"
        />
        <rect
          className="basket-opening"
          x="-21"
          y="-14"
          width="42"
          height="28"
          rx="3"
          fill="none"
          stroke={element.color}
          strokeWidth="2.5"
        />
      </g>
    );
  if (element.kind === "square")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="23" />
        <rect
          x="-12"
          y="-12"
          width="24"
          height="24"
          fill="none"
          stroke={element.color}
          strokeWidth="4"
        />
      </g>
    );
  if (element.kind === "cross")
    return (
      <g
        className={selected ? "selected" : ""}
        transform={markerTransform}
        onPointerDown={(event) => onPointerDown?.(element.id, event)}
      >
        <circle className="object-hit" r="23" />
        <path
          d="M-12-12L12 12M12-12L-12 12"
          stroke={element.color}
          strokeWidth="5"
          strokeLinecap="round"
        />
      </g>
    );
  const defaultLabel =
    element.kind === "goalie"
      ? "G"
      : element.kind === "coach"
        ? "C"
        : element.kind === "defender"
          ? "D"
          : "F";
  const shape =
    element.kind === "defender" ? (
      <path
        d="M0-15L15 13H-15Z"
        fill={element.outlined ? "white" : element.color}
        stroke={element.color}
        strokeWidth="3"
      />
    ) : (
      <circle
        r="15"
        fill={
          element.outlined || element.kind === "coach" ? "white" : element.color
        }
        stroke={element.color}
        strokeWidth="3"
      />
    );
  return (
    <g
      transform={markerTransform}
      className={selected ? "selected" : ""}
      onPointerDown={(event) => onPointerDown?.(element.id, event)}
    >
      <circle className="object-hit" r="23" />
      {shape}
      <text
        className="marker-label"
        x={element.number ? -3 : 0}
        y="1"
        fill={
          element.outlined || element.kind === "coach" ? element.color : "white"
        }
      >
        {element.label || defaultLabel}
      </text>
      {element.number &&
        (element.kind === "player" || element.kind === "defender") && (
          <text
            className="marker-number"
            x="8"
            y="7"
            fill={
              element.outlined ? element.color : "white"
            }
          >
            {element.number}
          </text>
        )}
    </g>
  );
}

export function RinkDiagram({
  template,
  rinkSide = "left",
  elements,
  selectedId,
  selectedIds = [],
  selectionBox,
  interactive,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onElementPointerDown,
  onTextChange,
}: Props) {
  const view = rinkViewport(template, rinkSide);
  return (
    <svg
      className={`rink-diagram rink-${template} rink-side-${rinkSide}${interactive ? " interactive" : ""}`}
      viewBox={`${view.x} ${view.y} ${view.width} ${view.height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${rinkSide} ${template} ice drill diagram`}
      onPointerDown={(event) =>
        onPointerDown?.(eventPoint(event, template, rinkSide), event)
      }
      onPointerMove={(event) =>
        onPointerMove?.(eventPoint(event, template, rinkSide), event)
      }
      onPointerUp={(event) =>
        onPointerUp?.(eventPoint(event, template, rinkSide), event)
      }
      onPointerCancel={(event) =>
        onPointerUp?.(eventPoint(event, template, rinkSide), event)
      }
    >
      <defs>
        <clipPath id="rink-inner-clip">
          <rect x="12" y="12" width="976" height="476" rx="88" />
        </clipPath>
        <marker
          id="line-arrow"
          markerWidth="9"
          markerHeight="9"
          refX="7"
          refY="3"
          orient="auto"
        >
          <path d="M0 0v6l8-3Z" fill="context-stroke" />
        </marker>
        <pattern
          id="ice-grid"
          width="30"
          height="30"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M30 0H0V30"
            fill="none"
            stroke="#dce8ef"
            strokeWidth="1"
            opacity=".28"
          />
        </pattern>
      </defs>
      <OfficialRink blank={template === "blank"} />
      {elements.map((element) => (
        <g
          key={element.id}
          className={
            element.id === "placement-preview" ? "placement-preview" : ""
          }
        >
          <Element
            element={element}
            selected={
              selectedId === element.id || selectedIds.includes(element.id)
            }
            onPointerDown={onElementPointerDown}
            onTextChange={onTextChange}
          />
        </g>
      ))}
      {selectionBox &&
        (() => {
          const box = normalizedSelectionBox(selectionBox);
          return (
            <rect
              className="marquee-selection"
              x={box.x}
              y={box.y}
              width={box.width}
              height={box.height}
            />
          );
        })()}
    </svg>
  );
}
